import { cloneDeep } from 'lodash';
import {
  bookingJourneyType,
  orderStage,
  orderSecondaryStage,
  instantBookOrderStage,
} from 'common/constants/booking';

const milestoneStatus = {
  TODO: 'todo',
  DOING: 'doing',
  DONE: 'done',
  FAILED: 'failed',
};

// mileStones show in UI
const mileStones = {
  CREATE: 'create',
  CONFIRMATION: 'confirmation',
  PAYMENT: 'payment',
  CHECK_IN: 'check_in',
  COMPLETED: 'completed',
  // instant book
  FILLING_PERSONAL_INFO: 'filling_personal_info',
  SIGNING_AGREEMENT: 'signing_agreement',
  MAKING_PAYMENT: 'making_payment',
  BOOKING_CONFIRMED: 'booking_confirmed',
  CHECKED_IN: 'checked_in',
  LOST: 'lost',
  REFUNDING: 'refunding',
  CANCELLED: 'cancelled',
};

// detail process flow refers to: https://oslcorp.atlassian.net/browse/SUN-6627
//
// workflow shows as below:
//
//                            +-------------+
//                  +-------->+    Lost     |
//                  |         +--------+----+
//                  |                  ^
//                  |                  |
//           Timeout/AP Reject    Timeout/ST Cancel
//                  |                  |
//                  |                  |
// +------------+   |  +------------+  |  +-------------+   +-------------+   +-----------+
// |   create   +---+->+   confirm  +--+->+   payment   +---+  check in   +-+>+  Complete |
// +------------+      +------------+     +--------------+ +--------------+ | +-----------+
//                                                       | |                |
//                                              ST cancel  AP cancel   ST/AP Cancel
//                                                       | |                |
//                                                       | +----------------+
//                                                       | |  after service |
//                                                       | +---+------------+
//                                                       |     |
//                                                       v     v
//                                              +--------+-----++   +-----------------+
//                                              |  refunding    +-->+  cancelled      |
//                                              +---------------+   +-----------------+
// drawed by http://asciiflow.com/

// orderStage和milestone的对应关系
// orderStages中的LOST、CANCELLED、AFTER_SALES_INTERVENTION、REFUNDING
// 需要根据secondaryStages或前面的stage流程来确定是哪一个milestone失败的;
// 然后在对应的milestone会显示红色代表失败
const stageMilestoneMapping = {
  [orderStage.WAITING_FOR_CONFIRMATION]: mileStones.CREATE,
  [orderStage.WAITING_FOR_PAYMENT]: mileStones.CONFIRMATION,
  [orderStage.WAITING_FOR_CHECK_IN]: mileStones.PAYMENT,
  [orderStage.CHECKED_IN]: mileStones.CHECK_IN,
  [orderStage.COMPLETED]: mileStones.COMPLETED,
  [instantBookOrderStage.FILLING_PERSONAL_INFO]: mileStones.FILLING_PERSONAL_INFO,
  [instantBookOrderStage.SIGNING_AGREEMENT]: mileStones.SIGNING_AGREEMENT,
  [instantBookOrderStage.MAKING_PAYMENT]: mileStones.MAKING_PAYMENT,
  [instantBookOrderStage.BOOKING_CONFIRMED]: mileStones.BOOKING_CONFIRMED,
};
const noPaymentStageMilestoneMapping = {
  [orderStage.WAITING_FOR_CONFIRMATION]: mileStones.CREATE,
  [orderStage.WAITING_FOR_CHECK_IN]: mileStones.CONFIRMATION,
  [orderStage.CHECKED_IN]: mileStones.CHECK_IN,
  [orderStage.COMPLETED]: mileStones.COMPLETED,
  [instantBookOrderStage.FILLING_PERSONAL_INFO]: mileStones.FILLING_PERSONAL_INFO,
  [instantBookOrderStage.SIGNING_AGREEMENT]: mileStones.SIGNING_AGREEMENT,
  [instantBookOrderStage.MAKING_PAYMENT]: mileStones.MAKING_PAYMENT,
  [instantBookOrderStage.BOOKING_CONFIRMED]: mileStones.BOOKING_CONFIRMED,
};
const commonMilestoneList = [
  mileStones.CREATE,
  mileStones.CONFIRMATION,
  mileStones.PAYMENT,
  mileStones.CHECK_IN,
  mileStones.COMPLETED,
];

const instantMilestoneList = [
  mileStones.FILLING_PERSONAL_INFO,
  mileStones.SIGNING_AGREEMENT,
  mileStones.MAKING_PAYMENT,
  mileStones.BOOKING_CONFIRMED,
  mileStones.COMPLETED,
  mileStones.CHECKED_IN,
];

const noPaymentMpMilestoneList = [
  mileStones.CREATE,
  mileStones.CONFIRMATION,
  mileStones.CHECK_IN,
  mileStones.COMPLETED,
];
const commonMilestoneDetailList = {
  [mileStones.CREATE]: { status: milestoneStatus.TODO, iconHash: 'progress-application-form' },
  [mileStones.CONFIRMATION]: {
    status: milestoneStatus.TODO,
    iconHash: 'progress-landlord-pre-approval',
  },
  [mileStones.PAYMENT]: { status: milestoneStatus.TODO, iconHash: 'progress-deposit-payment' },
  [mileStones.CHECK_IN]: {
    status: milestoneStatus.TODO,
    iconHash: 'longtail-process-check-in',
  },
  [mileStones.COMPLETED]: {
    status: milestoneStatus.TODO,
    iconHash: 'progress-booking-complete',
  },
};
const noPaymentMilestoneDetailList = {
  [mileStones.CREATE]: { status: milestoneStatus.TODO, iconHash: 'progress-application-form' },
  [mileStones.CONFIRMATION]: {
    status: milestoneStatus.TODO,
    iconHash: 'progress-landlord-pre-approval',
  },

  [mileStones.CHECK_IN]: {
    status: milestoneStatus.TODO,
    iconHash: 'longtail-process-check-in',
  },
  [mileStones.COMPLETED]: {
    status: milestoneStatus.TODO,
    iconHash: 'progress-booking-complete',
  },
};

const instantMilestoneDetailList = {
  [mileStones.FILLING_PERSONAL_INFO]: {
    status: milestoneStatus.TODO,
    iconHash: 'progress-fill-personal-info',
  },
  [mileStones.SIGNING_AGREEMENT]: {
    status: milestoneStatus.TODO,
    iconHash: 'progress-sign-contract',
  },
  [mileStones.MAKING_PAYMENT]: {
    status: milestoneStatus.TODO,
    iconHash: 'progress-making-payment',
  },
  [mileStones.BOOKING_CONFIRMED]: {
    status: milestoneStatus.TODO,
    iconHash: 'progress-booking-confirmed',
  },
  [mileStones.COMPLETED]: {
    status: milestoneStatus.TODO,
    iconHash: 'progress-booking-complete',
  },
  [mileStones.CHECKED_IN]: {
    status: milestoneStatus.TODO,
    iconHash: 'longtail-process-check-in',
  },
};

const useProcesses = ({
  processes = [],
  bookingJourney,
  stage,
  isPaymentOnline = true,
  isNoPaymentReselectPaymentPlan = false,
}) => {
  const isNoPayment = !isPaymentOnline;
  const isInstantBook = bookingJourney === bookingJourneyType.INSTANT_BOOK;

  const milestoneList = cloneDeep(
    isInstantBook
      ? instantMilestoneList
      : isPaymentOnline || isNoPaymentReselectPaymentPlan
      ? commonMilestoneList
      : noPaymentMpMilestoneList,
  );

  let milestoneDetailList = cloneDeep(
    isInstantBook
      ? instantMilestoneDetailList
      : isPaymentOnline || isNoPaymentReselectPaymentPlan
      ? commonMilestoneDetailList
      : noPaymentMilestoneDetailList,
  );

  if (isNoPaymentReselectPaymentPlan) {
    milestoneDetailList[mileStones.PAYMENT] = {
      ...milestoneDetailList[mileStones.PAYMENT],
      iconHash: 'reselect-payment-plan',
    };
  }
  const getMileStoneDetails = () => {
    let completeIndex = -1;

    if (isInstantBook) {
      const refundingTarget = processes.find(
        process => process.currentStage === instantBookOrderStage.REFUNDING,
      );
      const canceledTarget = processes.find(
        process => process.currentStage === instantBookOrderStage.CANCELLED,
      );

      if (refundingTarget) {
        milestoneDetailList = {
          [mileStones.REFUNDING]: {
            status: milestoneStatus.DOING,
            type: 'refund',
            updatedAt: refundingTarget.updatedAt,
            iconHash: 'progress-refunding',
            secondaryStage: 'refund_pending',
          },
          [mileStones.CANCELLED]: {
            status: milestoneStatus.TODO,
            iconHash: 'progress-instant-book-cancelled',
          },
        };
      }

      if (!isNoPayment && canceledTarget) {
        milestoneDetailList[mileStones.REFUNDING].status = milestoneStatus.DONE;
        milestoneDetailList[mileStones.REFUNDING].secondaryStage = 'refund_refunded';

        Object.assign(milestoneDetailList[mileStones.CANCELLED], {
          status: milestoneStatus.DONE,
          type: 'cancel',
          updatedAt: canceledTarget.updatedAt,
          secondaryStage: canceledTarget.currentSecondaryStage?.toLowerCase(),
        });
      }
      if (isNoPayment && canceledTarget) {
        milestoneDetailList = {
          [mileStones.CANCELLED]: {
            iconHash: 'progress-instant-book-cancelled',
            status: milestoneStatus.DONE,
            type: 'cancel',
            updatedAt: canceledTarget.updatedAt,
            secondaryStage: canceledTarget.currentSecondaryStage?.toLowerCase(),
          },
        };
      }
    }

    processes.forEach(process => {
      const { currentStage, updatedAt, currentSecondaryStage } = process;
      const nextMileStone = milestoneList[completeIndex + 1];

      if (milestoneDetailList[nextMileStone]) {
        if (
          isNoPayment && !isNoPaymentReselectPaymentPlan
            ? noPaymentStageMilestoneMapping[currentStage] === nextMileStone
            : stageMilestoneMapping[currentStage] === nextMileStone
        ) {
          Object.assign(milestoneDetailList[nextMileStone], {
            status: milestoneStatus.DONE,
            updatedAt,
            secondaryStage: currentSecondaryStage?.toLowerCase(),
          });
          completeIndex += 1;
        } else {
          if (isInstantBook) {
            if (currentStage === instantBookOrderStage.LOST) {
              const preMilestone = milestoneList[completeIndex];

              milestoneDetailList[mileStones.LOST] = {
                status: milestoneStatus.DONE,
                updatedAt,
                type: 'lost',
                iconHash: 'progress-instant-book-lost',
                secondaryStage: currentSecondaryStage?.toLowerCase(),
              };

              Object.assign(milestoneDetailList[preMilestone], {
                border: 'dotted',
                iconHash: 'progress-stop',
              });

              while (milestoneList[completeIndex + 1]) {
                delete milestoneDetailList[milestoneList[completeIndex + 1]];
                // milestoneList.splice(completeIndex, 1);

                completeIndex += 1;
              }
            }
          } else if (currentStage === orderStage.WAITING_FOR_CONFIRMATION) {
            Object.assign(milestoneDetailList[mileStones.CONFIRMATION], {
              updatedAt,
              secondaryStage: currentSecondaryStage?.toLowerCase(),
            });
          } else {
            Object.assign(milestoneDetailList[nextMileStone], {
              status: milestoneStatus.FAILED,
              updatedAt,
            });
          }
        }
      }
    });

    // change next milestone of last done milestone into DOING
    milestoneList.forEach((milestone, index) => {
      const preMilestone = milestoneList[index - 1];

      if (
        preMilestone &&
        !isInstantBook &&
        milestoneDetailList[milestone]?.status === milestoneStatus.TODO &&
        milestoneDetailList[preMilestone]?.status === milestoneStatus.DONE
      ) {
        milestoneDetailList[milestone].status = milestoneStatus.DOING;

        if (
          [mileStones.CONFIRMATION, mileStones.PAYMENT, mileStones.COMPLETED].includes(milestone)
        ) {
          // counter offer countdown
          if (
            milestoneDetailList[milestone]?.secondaryStage ===
            orderSecondaryStage.PENDING_COUNTER_OFFER?.toLowerCase()
          ) {
            // add countdown
            milestoneDetailList[milestone].countdown = {
              initTime: milestoneDetailList[milestone].updatedAt,
            };
          } else {
            // add countdown
            milestoneDetailList[milestone].countdown = {
              initTime: milestoneDetailList[preMilestone].updatedAt,
            };
          }
        }
      }
    });

    // remove pending counter offer after pending
    if (
      stage &&
      stage !== orderStage.WAITING_FOR_CONFIRMATION &&
      milestoneDetailList[mileStones.CONFIRMATION]?.secondaryStage
    ) {
      milestoneDetailList[mileStones.CONFIRMATION].secondaryStage = null;
    }

    if (
      isInstantBook &&
      stage !== instantBookOrderStage.CANCELLED &&
      milestoneDetailList[mileStones?.[stage]]
    ) {
      milestoneDetailList[mileStones[stage]].status = milestoneStatus.DOING;
    }

    return milestoneDetailList;
  };

  return {
    milestoneDetails: getMileStoneDetails(),
    milestoneStatus,
  };
};

export default useProcesses;
